import React, { useState, useMemo, useEffect } from "react";
import { LoteDetailResponse, ItemConsolidado } from "../types";
import {
  X,
  Image as ImageIcon,
  AlertTriangle,
  Layers,
  Calculator,
  Search,
  Sparkles,
  FileText,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  PackageCheck,
  Tag,
  ArrowUpDown,
  Loader2,
} from "lucide-react";
import { SimuladorLance } from "./SimuladorLance";

function normalizarTexto(desc: string): string {
  if (!desc) return "";
  let s = desc.toUpperCase().trim();
  s = s.replace(/\s*\/\/ID.*$/i, "");
  s = s.replace(/\s*\/\/LOTE.*$/i, "");
  s = s.replace(/(\d+)\s*(ML|L|G|KG|UN|PC|PCS|VI)\b/gi, "$1 $2");
  s = s.replace(/\s+/g, " ");
  return s.trim();
}

interface LoteModalProps {
  detail: LoteDetailResponse | null;
  initialTab?: "itens" | "markup" | "fotos";
  onClose: () => void;
  onNavigate?: (loteId: string, currentTab: "itens" | "markup" | "fotos") => void;
  isLoadingAdjacent?: boolean;
}

export const LoteModal: React.FC<LoteModalProps> = ({
  detail,
  initialTab,
  onClose,
  onNavigate,
  isLoadingAdjacent = false,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"itens" | "markup" | "fotos">(initialTab || "itens");

  // Atualiza activeTab se initialTab mudar externamente
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Navegação rápida via teclado (← Seta Esquerda / Seta Direita →)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (e.key === "ArrowLeft" && detail?.navegacao?.anterior && onNavigate && !isLoadingAdjacent) {
        onNavigate(detail.navegacao.anterior.id, activeTab);
      } else if (e.key === "ArrowRight" && detail?.navegacao?.proximo && onNavigate && !isLoadingAdjacent) {
        onNavigate(detail.navegacao.proximo.id, activeTab);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [detail?.navegacao, onNavigate, activeTab, onClose, isLoadingAdjacent]);

  // Estados da Visão Consolidada
  const [modoVisao, setModoVisao] = useState<"consolidada" | "detalhada">("consolidada");
  const [busca, setBusca] = useState("");
  const [marcaSelecionada, setMarcaSelecionada] = useState<string | null>(null);
  const [ordenacao, setOrdenacao] = useState<"qtd_desc" | "qtd_asc" | "nome_asc" | "valor_desc">("qtd_desc");
  const [itemExpandidoId, setItemExpandidoId] = useState<string | null>(null);

  const lote = detail?.lote;
  const itens = Array.isArray(detail?.itens) ? detail.itens : [];
  const avisos = Array.isArray(detail?.avisos) ? detail.avisos : [];
  const imagens = Array.isArray(detail?.imagens) ? detail.imagens : [];

  const formatMoney = (val: number | null | undefined) => {
    const num = Number(val);
    if (val === null || val === undefined || isNaN(num)) return "N/D";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(num);
  };

  // 1. Itens Consolidados (da API ou calculados como fallback)
  const itensConsolidados = useMemo<ItemConsolidado[]>(() => {
    if (!lote) return [];
    if (detail?.itens_consolidados && detail.itens_consolidados.length > 0) {
      return detail.itens_consolidados;
    }
    const totalUnidades = Math.max(1, lote.qtde_total_unidades || 1);
    const custoUnit = lote.custo_unitario_estimado || (lote.valor_minimo / totalUnidades);
    const map = new Map<string, { desc: string; marca: string; cat: string; un: string; qtd: number; ocorrencias: number }>();

    for (const it of itens) {
      const descNorm = normalizarTexto(it.descricao);
      const unNorm = (it.un_medida || "un").toLowerCase().trim();
      const key = `${descNorm}:::${unNorm}`;
      if (!map.has(key)) {
        map.set(key, {
          desc: descNorm,
          marca: it.marca || "Não identificada",
          cat: it.categoria || lote.tipo || "Outros",
          un: unNorm,
          qtd: it.quantidade,
          ocorrencias: 1,
        });
      } else {
        const entry = map.get(key)!;
        entry.qtd += it.quantidade;
        entry.ocorrencias += 1;
        if (entry.marca === "Não identificada" && it.marca && it.marca !== "Não identificada") {
          entry.marca = it.marca;
        }
      }
    }

    return Array.from(map.values())
      .map((entry, idx) => ({
        id: `cons-${lote.id}-${idx + 1}`,
        descricao: entry.desc,
        marca: entry.marca,
        categoria: entry.cat,
        un_medida: entry.un,
        quantidade_total: entry.qtd,
        total_ocorrencias: entry.ocorrencias,
        custo_unitario_estimado: Number(custoUnit.toFixed(2)),
        custo_total_estimado: Number((custoUnit * entry.qtd).toFixed(2)),
        percentual_do_lote: Number(((entry.qtd / totalUnidades) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.quantidade_total - a.quantidade_total);
  }, [detail, lote, itens]);

  // 2. Resumo de Marcas
  const resumoMarcas = useMemo(() => {
    if (!lote) return [];
    if (detail?.resumo_marcas && detail.resumo_marcas.length > 0) {
      return detail.resumo_marcas;
    }
    const totalUnidades = Math.max(1, lote.qtde_total_unidades || 1);
    const map = new Map<string, { qtd: number; prods: Set<string> }>();
    for (const it of itensConsolidados) {
      const m = it.marca || "Não identificada";
      if (!map.has(m)) map.set(m, { qtd: 0, prods: new Set() });
      const reg = map.get(m)!;
      reg.qtd += it.quantidade_total;
      reg.prods.add(it.descricao);
    }
    return Array.from(map.entries())
      .map(([marca, data]) => ({
        marca,
        total_unidades: data.qtd,
        produtos_distintos: data.prods.size,
        percentual_do_lote: Number(((data.qtd / totalUnidades) * 100).toFixed(1)),
      }))
      .sort((a, b) => b.total_unidades - a.total_unidades);
  }, [detail, itensConsolidados, lote?.qtde_total_unidades]);

  // 3. Estatísticas de Consolidação
  const estatisticas = useMemo(() => {
    if (!lote) {
      return { total_linhas_rfb: 0, total_produtos_unicos: 0, total_unidades: 0, taxa_reducao_duplicatas: 0 };
    }
    if (detail?.estatisticas_consolidacao) {
      return detail.estatisticas_consolidacao;
    }
    const totalLinhas = itens.length;
    const totalUnicos = itensConsolidados.length;
    return {
      total_linhas_rfb: totalLinhas,
      total_produtos_unicos: totalUnicos,
      total_unidades: lote.qtde_total_unidades,
      taxa_reducao_duplicatas:
        totalLinhas > 0 ? Number((((totalLinhas - totalUnicos) / totalLinhas) * 100).toFixed(1)) : 0,
    };
  }, [detail, itens.length, itensConsolidados.length, lote?.qtde_total_unidades]);

  // 4. Filtragem da Visão Consolidada
  const itensConsolidadosFiltrados = useMemo(() => {
    return itensConsolidados
      .filter((it) => {
        const matchBusca =
          !busca ||
          it.descricao.toLowerCase().includes(busca.toLowerCase()) ||
          it.marca.toLowerCase().includes(busca.toLowerCase());
        const matchMarca = !marcaSelecionada || it.marca === marcaSelecionada;
        return matchBusca && matchMarca;
      })
      .sort((a, b) => {
        if (ordenacao === "qtd_desc") return b.quantidade_total - a.quantidade_total;
        if (ordenacao === "qtd_asc") return a.quantidade_total - b.quantidade_total;
        if (ordenacao === "nome_asc") return a.descricao.localeCompare(b.descricao);
        if (ordenacao === "valor_desc") return (b.custo_total_estimado || 0) - (a.custo_total_estimado || 0);
        return 0;
      });
  }, [itensConsolidados, busca, marcaSelecionada, ordenacao]);

  // 5. Filtragem da Visão Detalhada (Lançamentos RFB)
  const itensDetalhadosFiltrados = useMemo(() => {
    return itens.filter((it) => {
      const matchBusca =
        !busca ||
        it.descricao.toLowerCase().includes(busca.toLowerCase()) ||
        it.marca.toLowerCase().includes(busca.toLowerCase());
      const matchMarca = !marcaSelecionada || it.marca === marcaSelecionada;
      return matchBusca && matchMarca;
    });
  }, [itens, busca, marcaSelecionada]);

  // Função para buscar ocorrências brutas de um item consolidado
  const getOcorrenciasBrutas = (descConsolidada: string) => {
    return itens.filter((raw) => normalizarTexto(raw.descricao) === descConsolidada);
  };

  if (!detail || !lote) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
        {isLoadingAdjacent && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(17, 24, 39, 0.65)",
              backdropFilter: "blur(2px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100,
              borderRadius: "20px",
              gap: "12px",
            }}
          >
            <Loader2 className="animate-spin" size={36} color="#3b82f6" />
            <span style={{ fontSize: "0.9rem", color: "#93c5fd", fontWeight: 600 }}>Carregando lote...</span>
          </div>
        )}
        {/* Modal Header */}
        <header className="modal-header">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <h2 style={{ fontSize: "1.4rem", fontWeight: 800, color: "var(--primary)", margin: 0 }}>
                Lote #{lote.nr_atribuido}
              </h2>
              <span className="badge badge-ativo">{lote.tipo || "DIVERSOS"}</span>
              {lote.permite_pf === 1 ? (
                <span className="badge badge-pf">Pessoa Física & Jurídica</span>
              ) : (
                <span className="badge badge-pj">Exclusivo PJ</span>
              )}
            </div>
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "4px", margin: "4px 0 0 0" }}>
              Edital {lote.edital_id} {lote.cidade && `• ${lote.cidade}`} • Lance Mínimo: <strong>{formatMoney(lote.valor_minimo)}</strong>
              {lote.valor_avaliacao && (
                <> • Avaliação RFB: <strong style={{ color: "#93c5fd" }}>{formatMoney(lote.valor_avaliacao)}</strong></>
              )}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* Atalhos para navegar entre lotes diretamente */}
            {onNavigate && (
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={!detail.navegacao?.anterior || isLoadingAdjacent}
                  onClick={() => {
                    if (detail.navegacao?.anterior) {
                      onNavigate(detail.navegacao.anterior.id, activeTab);
                    }
                  }}
                  title={
                    detail.navegacao?.anterior
                      ? `Ir para Lote #${detail.navegacao.anterior.nr_atribuido} (Seta ←)`
                      : "Primeiro lote deste edital"
                  }
                  style={{
                    padding: "6px 12px",
                    fontSize: "0.82rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    opacity: !detail.navegacao?.anterior || isLoadingAdjacent ? 0.45 : 1,
                    cursor: !detail.navegacao?.anterior || isLoadingAdjacent ? "not-allowed" : "pointer",
                    background: "rgba(31, 41, 55, 0.7)",
                  }}
                >
                  <ChevronLeft size={16} /> Lote #{detail.navegacao?.anterior?.nr_atribuido ?? "—"}
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={!detail.navegacao?.proximo || isLoadingAdjacent}
                  onClick={() => {
                    if (detail.navegacao?.proximo) {
                      onNavigate(detail.navegacao.proximo.id, activeTab);
                    }
                  }}
                  title={
                    detail.navegacao?.proximo
                      ? `Ir para Lote #${detail.navegacao.proximo.nr_atribuido} (Seta →)`
                      : "Último lote deste edital"
                  }
                  style={{
                    padding: "6px 12px",
                    fontSize: "0.82rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    opacity: !detail.navegacao?.proximo || isLoadingAdjacent ? 0.45 : 1,
                    cursor: !detail.navegacao?.proximo || isLoadingAdjacent ? "not-allowed" : "pointer",
                    background: "rgba(31, 41, 55, 0.7)",
                  }}
                >
                  Lote #{detail.navegacao?.proximo?.nr_atribuido ?? "—"} <ChevronRight size={16} />
                </button>
              </div>
            )}

            <button className="btn-icon" onClick={onClose} title="Fechar (Esc)">
              <X size={24} />
            </button>
          </div>
        </header>

        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            padding: "0 24px",
            borderBottom: "1px solid var(--border-color)",
            background: "var(--bg-secondary)",
          }}
        >
          <button
            onClick={() => setActiveTab("itens")}
            style={{
              padding: "12px 16px",
              background: "transparent",
              border: "none",
              borderBottom: activeTab === "itens" ? "2px solid #3b82f6" : "2px solid transparent",
              color: activeTab === "itens" ? "#60a5fa" : "var(--text-muted)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Layers size={16} /> O que compõe o Lote ({estatisticas.total_produtos_unicos} produtos)
          </button>

          <button
            onClick={() => setActiveTab("markup")}
            style={{
              padding: "12px 16px",
              background: "transparent",
              border: "none",
              borderBottom: activeTab === "markup" ? "2px solid #3b82f6" : "2px solid transparent",
              color: activeTab === "markup" ? "#60a5fa" : "var(--text-muted)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Calculator size={16} /> Simulador de Lance & Custo
          </button>

          {(imagens.length > 0 || avisos.length > 0) && (
            <button
              onClick={() => setActiveTab("fotos")}
              style={{
                padding: "12px 16px",
                background: "transparent",
                border: "none",
                borderBottom: activeTab === "fotos" ? "2px solid #f59e0b" : "2px solid transparent",
                color: activeTab === "fotos" ? "#fbbf24" : "var(--text-muted)",
                fontWeight: 700,
                fontSize: "0.85rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <ImageIcon size={16} /> Fotos & Avisos ({imagens.length + avisos.length})
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* TAB 1: ITENS COM VISÃO CONSOLIDADA */}
          {activeTab === "itens" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* 1. CARDS DE MÉTRICAS DE CONSOLIDAÇÃO */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    background: "var(--bg-card)",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
                    Total Físico de Unidades
                  </div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, fontFamily: "var(--font-mono)", color: "#38bdf8", marginTop: "2px" }}>
                    {(Number(lote.qtde_total_unidades) || 0).toLocaleString("pt-BR")} <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>un</span>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                    Custo Médio: <strong>{formatMoney(lote.custo_unitario_estimado)}</strong>/un
                  </div>
                </div>

                <div
                  style={{
                    background: "var(--bg-card)",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    border: "1px solid rgba(56, 189, 248, 0.3)",
                  }}
                >
                  <div style={{ fontSize: "0.74rem", color: "#38bdf8", textTransform: "uppercase", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px" }}>
                    <PackageCheck size={14} /> Produtos Únicos
                  </div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, fontFamily: "var(--font-mono)", color: "#f8fafc", marginTop: "2px" }}>
                    {estatisticas.total_produtos_unicos}{" "}
                    <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>modelos distintos</span>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "#34d399", marginTop: "2px" }}>
                    Visão agrupada e limpa
                  </div>
                </div>

                <div
                  style={{
                    background: "var(--bg-card)",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
                    Lançamentos no Edital RFB
                  </div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, fontFamily: "var(--font-mono)", color: "#cbd5e1", marginTop: "2px" }}>
                    {estatisticas.total_linhas_rfb}{" "}
                    <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>registros brutos</span>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                    Termos de apreensão
                  </div>
                </div>

                <div
                  style={{
                    background: "var(--bg-card)",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
                    Consolidação Inteligente
                  </div>
                  <div style={{ fontSize: "1.3rem", fontWeight: 800, fontFamily: "var(--font-mono)", color: "#a78bfa", marginTop: "2px" }}>
                    -{estatisticas.taxa_reducao_duplicatas}%{" "}
                    <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>duplicidades</span>
                  </div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: "2px" }}>
                    Repetições somadas por modelo
                  </div>
                </div>
              </div>

              {/* 2. FILTRO RÁPIDO POR MARCAS */}
              {resumoMarcas.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                    background: "rgba(15, 23, 42, 0.4)",
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700 }}>
                    <Tag size={12} color="#38bdf8" /> Filtrar por Marca Predominante
                  </div>
                  <div style={{ display: "flex", gap: "6px", overflowX: "auto", paddingBottom: "4px" }}>
                    <button
                      onClick={() => setMarcaSelecionada(null)}
                      style={{
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        border: !marcaSelecionada ? "1px solid #38bdf8" : "1px solid var(--border-color)",
                        background: !marcaSelecionada ? "rgba(56, 189, 248, 0.15)" : "var(--bg-card)",
                        color: !marcaSelecionada ? "#38bdf8" : "var(--text-muted)",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Todas ({(Number(lote.qtde_total_unidades) || 0).toLocaleString("pt-BR")} un)
                    </button>
                    {resumoMarcas.map((m) => (
                      <button
                        key={m.marca}
                        onClick={() => setMarcaSelecionada(marcaSelecionada === m.marca ? null : m.marca)}
                        style={{
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "0.74rem",
                          fontWeight: 700,
                          border: marcaSelecionada === m.marca ? "1px solid #38bdf8" : "1px solid var(--border-color)",
                          background: marcaSelecionada === m.marca ? "rgba(56, 189, 248, 0.15)" : "var(--bg-card)",
                          color: marcaSelecionada === m.marca ? "#38bdf8" : "var(--text-main)",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <span>{m.marca}</span>
                        <span style={{ color: "#94a3b8", fontFamily: "var(--font-mono)", fontSize: "0.7rem" }}>
                          {(Number(m.total_unidades) || 0).toLocaleString("pt-BR")} un ({m.percentual_do_lote}%)
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 3. BARRA DE CONTROLES: BUSCA, VISÃO E ORDENAÇÃO */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                {/* Alternador de Modo de Visão */}
                <div style={{ display: "flex", background: "var(--bg-secondary)", borderRadius: "8px", padding: "3px", border: "1px solid var(--border-color)" }}>
                  <button
                    onClick={() => setModoVisao("consolidada")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "none",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      background: modoVisao === "consolidada" ? "rgba(56, 189, 248, 0.2)" : "transparent",
                      color: modoVisao === "consolidada" ? "#38bdf8" : "var(--text-muted)",
                    }}
                  >
                    <Sparkles size={14} /> Visão Consolidada ({itensConsolidadosFiltrados.length})
                  </button>

                  <button
                    onClick={() => setModoVisao("detalhada")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      border: "none",
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      cursor: "pointer",
                      background: modoVisao === "detalhada" ? "rgba(255, 255, 255, 0.1)" : "transparent",
                      color: modoVisao === "detalhada" ? "#f8fafc" : "var(--text-muted)",
                    }}
                  >
                    <FileText size={14} /> Lançamentos RFB ({itensDetalhadosFiltrados.length})
                  </button>
                </div>

                {/* Filtro de Busca e Ordenação */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ position: "relative" }}>
                    <Search
                      size={14}
                      color="var(--text-muted)"
                      style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)" }}
                    />
                    <input
                      type="text"
                      placeholder="Buscar mercadoria ou modelo..."
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      style={{
                        padding: "6px 10px 6px 30px",
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderRadius: "8px",
                        fontSize: "0.8rem",
                        color: "var(--text-main)",
                        width: "220px",
                      }}
                    />
                  </div>

                  {modoVisao === "consolidada" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <ArrowUpDown size={14} color="var(--text-muted)" />
                      <select
                        value={ordenacao}
                        onChange={(e) => setOrdenacao(e.target.value as any)}
                        style={{
                          padding: "6px 8px",
                          background: "var(--bg-secondary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "8px",
                          fontSize: "0.8rem",
                          color: "var(--text-main)",
                          cursor: "pointer",
                        }}
                      >
                        <option value="qtd_desc">Maior Quantidade</option>
                        <option value="qtd_asc">Menor Quantidade</option>
                        <option value="nome_asc">Nome (A-Z)</option>
                        <option value="valor_desc">Maior Valor Subtotal</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* 4. TABELA CONSOLIDADA */}
              {modoVisao === "consolidada" && (
                <div style={{ overflowX: "auto", border: "1px solid var(--border-color)", borderRadius: "12px" }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: "30px" }}></th>
                        <th>Produto Consolidado</th>
                        <th>Marca</th>
                        <th>Categoria</th>
                        <th style={{ textAlign: "right" }}>Quantidade Somada</th>
                        <th style={{ textAlign: "right" }}>Fatia no Lote</th>
                        <th style={{ textAlign: "right" }}>Custo Médio</th>
                        <th style={{ textAlign: "right" }}>Custo Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensConsolidadosFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", padding: "24px", color: "var(--text-muted)" }}>
                            Nenhum produto encontrado para os filtros selecionados.
                          </td>
                        </tr>
                      ) : (
                        itensConsolidadosFiltrados.map((it) => {
                          const isExpanded = itemExpandidoId === it.id;
                          const rawMatches = it.total_ocorrencias > 1 ? getOcorrenciasBrutas(it.descricao) : [];

                          return (
                            <React.Fragment key={it.id}>
                              <tr
                                onClick={() => {
                                  if (it.total_ocorrencias > 1) {
                                    setItemExpandidoId(isExpanded ? null : it.id);
                                  }
                                }}
                                style={{
                                  cursor: it.total_ocorrencias > 1 ? "pointer" : "default",
                                  background: isExpanded ? "rgba(56, 189, 248, 0.05)" : undefined,
                                }}
                              >
                                <td style={{ textAlign: "center", padding: "8px 4px" }}>
                                  {it.total_ocorrencias > 1 ? (
                                    isExpanded ? <ChevronDown size={14} color="#38bdf8" /> : <ChevronRight size={14} color="var(--text-muted)" />
                                  ) : null}
                                </td>
                                <td>
                                  <div style={{ fontWeight: 600, color: "#f8fafc" }}>{it.descricao}</div>
                                  {it.total_ocorrencias > 1 && (
                                    <div style={{ fontSize: "0.72rem", color: "#38bdf8", marginTop: "2px" }}>
                                      🔁 {it.total_ocorrencias} lançamentos do edital somados (clique para ver termos)
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span style={{ background: "rgba(96, 165, 250, 0.12)", color: "#60a5fa", padding: "2px 8px", borderRadius: "6px", fontSize: "0.78rem", fontWeight: 600 }}>
                                    {it.marca}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ background: "rgba(167, 139, 250, 0.12)", color: "#c084fc", padding: "2px 8px", borderRadius: "6px", fontSize: "0.78rem" }}>
                                    {it.categoria}
                                  </span>
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: "0.95rem", color: "#f8fafc" }}>
                                  {(Number(it.quantidade_total) || 0).toLocaleString("pt-BR")}{" "}
                                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", fontWeight: 400 }}>{it.un_medida}</span>
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)" }}>
                                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px" }}>
                                    <div
                                      style={{
                                        width: "40px",
                                        height: "6px",
                                        background: "rgba(255, 255, 255, 0.1)",
                                        borderRadius: "3px",
                                        overflow: "hidden",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${Math.min(100, it.percentual_do_lote)}%`,
                                          height: "100%",
                                          background: "#38bdf8",
                                          borderRadius: "3px",
                                        }}
                                      />
                                    </div>
                                    <span style={{ fontSize: "0.8rem", color: "#94a3b8" }}>{it.percentual_do_lote}%</span>
                                  </div>
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: "#34d399", fontSize: "0.85rem" }}>
                                  {formatMoney(it.custo_unitario_estimado)}
                                </td>
                                <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 700, color: "#cbd5e1" }}>
                                  {formatMoney(it.custo_total_estimado)}
                                </td>
                              </tr>

                              {/* ACCORDION: LANÇAMENTOS BRUTOS DO EDITAL */}
                              {isExpanded && (
                                <tr>
                                  <td colSpan={8} style={{ background: "rgba(11, 15, 25, 0.8)", padding: "12px 20px" }}>
                                    <div style={{ fontSize: "0.75rem", fontWeight: 700, color: "#38bdf8", marginBottom: "8px", textTransform: "uppercase" }}>
                                      📦 Detalhamento das {rawMatches.length} apreensões que compõem este produto no Edital:
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                      {rawMatches.map((raw, idx) => (
                                        <div
                                          key={raw.id || idx}
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            background: "rgba(255, 255, 255, 0.03)",
                                            padding: "6px 12px",
                                            borderRadius: "6px",
                                            border: "1px solid rgba(255, 255, 255, 0.05)",
                                            fontSize: "0.78rem",
                                          }}
                                        >
                                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
                                              Lançamento #{idx + 1}
                                            </span>
                                            <span style={{ color: "#cbd5e1" }}>{raw.descricao}</span>
                                          </div>
                                          <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "#34d399" }}>
                                            {(Number(raw.quantidade) || 0).toLocaleString("pt-BR")} {raw.un_medida || "un"}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 5. TABELA DETALHADA RFB (ORIGINAL) */}
              {modoVisao === "detalhada" && (
                <div style={{ overflowX: "auto", border: "1px solid var(--border-color)", borderRadius: "12px" }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nº</th>
                        <th>Mercadoria / Descrição Bruta no Edital</th>
                        <th>Marca</th>
                        <th>Categoria</th>
                        <th style={{ textAlign: "right" }}>Quantidade</th>
                        <th style={{ textAlign: "right" }}>Custo Estimado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensDetalhadosFiltrados.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center", padding: "24px", color: "var(--text-muted)" }}>
                            Nenhum registro encontrado.
                          </td>
                        </tr>
                      ) : (
                        itensDetalhadosFiltrados.map((it, idx) => (
                          <tr key={it.id || idx}>
                            <td style={{ fontFamily: "var(--font-mono)", color: "var(--text-dim)", fontSize: "0.75rem" }}>
                              #{idx + 1}
                            </td>
                            <td style={{ fontWeight: 600 }}>{it.descricao}</td>
                            <td>
                              <span style={{ background: "rgba(96, 165, 250, 0.1)", color: "#60a5fa", padding: "2px 8px", borderRadius: "6px", fontSize: "0.8rem" }}>
                                {it.marca}
                              </span>
                            </td>
                            <td>
                              <span style={{ background: "rgba(167, 139, 250, 0.1)", color: "#c084fc", padding: "2px 8px", borderRadius: "6px", fontSize: "0.8rem" }}>
                                {it.categoria}
                              </span>
                            </td>
                            <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 700 }}>
                              {(Number(it.quantidade) || 0).toLocaleString("pt-BR")} {it.un_medida || "un"}
                            </td>
                            <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", color: "#34d399" }}>
                              {formatMoney(it.custo_unitario_estimado)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: SIMULADOR DE LANCE */}
          {activeTab === "markup" && (
            <SimuladorLance lote={lote} itens={itens} />
          )}

          {/* TAB 3: FOTOS & AVISOS */}
          {activeTab === "fotos" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {avisos.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <h3 style={{ fontSize: "0.95rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px", color: "var(--accent-amber)" }}>
                    <AlertTriangle size={18} /> Avisos e Erratas Oficiais da RFB
                  </h3>
                  {avisos.map((av) => (
                    <div
                      key={av.id}
                      style={{
                        background: "rgba(245, 158, 11, 0.08)",
                        border: "1px solid rgba(245, 158, 11, 0.25)",
                        borderRadius: "10px",
                        padding: "12px 16px",
                        fontSize: "0.85rem",
                        color: "#fde68a",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "0.75rem", color: "var(--text-muted)" }}>
                        <span>{av.tipo}</span>
                        {av.data && <span>{av.data}</span>}
                      </div>
                      <div>{av.texto}</div>
                    </div>
                  ))}
                </div>
              )}

              {imagens.length > 0 && (
                <div>
                  <h3 style={{ fontSize: "0.95rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                    <ImageIcon size={18} color="#60a5fa" /> Galeria Oficial ({imagens.length})
                  </h3>
                  <div style={{ display: "flex", gap: "12px", overflowX: "auto", paddingBottom: "8px" }}>
                    {imagens.map((img) => (
                      <div
                        key={img.id}
                        onClick={() => setSelectedImage(img.url_grande)}
                        style={{
                          border: "1px solid var(--border-color)",
                          borderRadius: "10px",
                          overflow: "hidden",
                          cursor: "pointer",
                          width: "140px",
                          height: "100px",
                          flexShrink: 0,
                          background: "black",
                        }}
                      >
                        <img
                          src={img.url_miniatura}
                          alt="Foto oficial do lote"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedImage && (
                <div
                  style={{
                    position: "relative",
                    background: "black",
                    borderRadius: "12px",
                    overflow: "hidden",
                    textAlign: "center",
                    padding: "12px",
                  }}
                >
                  <button
                    className="btn-icon"
                    onClick={() => setSelectedImage(null)}
                    style={{ position: "absolute", top: "16px", right: "16px", background: "rgba(0,0,0,0.6)" }}
                  >
                    <X size={20} color="white" />
                  </button>
                  <img
                    src={selectedImage}
                    alt="Foto ampliada do lote"
                    style={{ maxHeight: "400px", maxWidth: "100%", objectFit: "contain" }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
